import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Loader, Plus, Save, Send, Mail, X, Trash2, FileText, ArrowLeft, Upload, RotateCcw, AlertTriangle, CheckCircle,
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { uprightJpegBytes } from '../utils/imageOrient';

// Incident Reports — the Safety page's tab for the formal write-up that goes to
// Australia Post. AP gives us no template, so this form is ours: fill it in here, check
// the PDF, then email it (with the photos) to whichever AP contacts need it.
//
// Backed by incident-reports-sb (settings KV, admin only). The address book is the one
// the Issue Register uses — the same AP managers, saved once.

const URL = '/.netlify/functions/incident-reports-sb';
const api = (action, payload = {}) =>
  fetch(URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...payload }) }).then(r => r.json());

const labelStyle = { display: 'block', fontSize: '.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--hp-dim)', marginBottom: 4 };
const inputStyle = { width: '100%', boxSizing: 'border-box' };
const th = { textAlign: 'left', padding: '9px 11px', fontSize: '.66rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--hp-dim)', borderBottom: '1px solid var(--hp-border)' };
const td = { padding: '9px 11px', fontSize: '.83rem', borderBottom: '1px solid var(--hp-border)', verticalAlign: 'top' };
const card = { background: 'var(--hp-card)', border: '1px solid var(--hp-border)', borderRadius: 12, padding: 16, marginBottom: 14 };
const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 };
const modalOverlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.78)', zIndex: 1200, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '32px 12px', overflowY: 'auto' };
const modalCard = { width: 620, maxWidth: '100%', background: 'var(--hp-card)', color: 'var(--hp-text)', borderRadius: 14, border: '1px solid var(--hp-border)', boxShadow: '0 24px 60px rgba(0,0,0,0.5)' };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const STATUS = { draft: ['Draft', '#d97706'], sent: ['Sent to AP', '#16a34a'], amended: ['Amended since sent', '#2563eb'] };
const Chip = ({ text, color }) => (
  <span style={{ fontSize: '.66rem', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.05em', color, border: `1px solid ${color}`, borderRadius: 5, padding: '2px 6px', whiteSpace: 'nowrap' }}>{text}</span>
);
const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(`${d}T00:00:00`);
  return isNaN(dt) ? d : dt.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
};
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString('en-AU', { day: 'numeric', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '');
const sydToday = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Australia/Sydney' });
const optLabel = (opts, v) => ((opts || []).find(([val]) => val === v) || [])[1] || '';

function blankForm(prev, me) {
  return {
    incidentType: '', incidentDate: sydToday(), incidentTime: '', location: '', suburb: '', locationType: '',
    area: '', conditions: '', reportedToOffice: '',
    driverLogin: '', driverName: '', driverRole: 'Parcel Contractor driver', driverPhone: '', activity: '',
    description: '',
    injured: '', injuryDetail: '', bodyParts: '', treatment: '', treatmentDetail: '', lostTime: '', daysOff: '', returnToWork: '',
    tpType: '', tpName: '', tpPhone: '', tpDescription: '', tpStayed: '',
    policeReported: '', policeEvent: '', policeStation: '', safeworkNotifiable: '', safeworkRef: '',
    articlesAffected: '', immediateActions: '', preventiveActions: '',
    // Whoever wrote the last one probably writes this one too.
    preparedBy: (prev && prev.preparedBy) || (me && me.name) || '',
    preparedPosition: (prev && prev.preparedPosition) || '',
    preparedPhone: (prev && prev.preparedPhone) || '',
    preparedEmail: (prev && prev.preparedEmail) || '',
    witnesses: [],
  };
}

export default function IncidentReportsTab({ notify }) {
  const { currentUser } = useApp();
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);     // report id, 'new', or null for the list
  const [seed, setSeed] = useState(null);         // { form, safetyReportId } for a new one
  const [showDeleted, setShowDeleted] = useState(false);

  const load = useCallback(async () => {
    const r = await api('list');
    if (r.success) setData(r); else notify(r.error || 'Failed to load incident reports', 'error');
    return r;
  }, [notify]);
  useEffect(() => { load(); }, [load]);

  const reports = useMemo(() => (data ? data.reports : []), [data]);
  const lastPrepared = useMemo(() => (reports.find(r => r.form && r.form.preparedBy) || {}).form, [reports]);

  if (!data) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--hp-dim)' }}><Loader size={18} /> Loading…</div>;

  const startNew = (safety) => {
    const f = blankForm(lastPrepared, currentUser);
    if (safety) {
      const d = data.drivers.find(x => x.login === (safety.driver_login || '').toUpperCase()) || {};
      Object.assign(f, {
        incidentType: safety.type === 'near_miss' ? 'near_miss' : safety.type === 'dog' ? 'animal' : safety.type === 'abuse' ? 'assault' : '',
        incidentDate: safety.occurred_on || f.incidentDate, incidentTime: safety.occurred_at || '',
        location: safety.address || '', suburb: safety.suburb || '',
        driverLogin: d.login || (safety.driver_login || '').toUpperCase(), driverName: d.name || safety.driver_name || '',
        driverPhone: d.phone || '', area: d.area || '',
        description: safety.description || '',
        injured: safety.injured ? 'yes' : '', injuryDetail: safety.injury_detail || '',
        immediateActions: safety.action_taken || '',
        witnesses: (safety.witnesses || []).map(w => ({ name: w.name || '', phone: w.phone || '', statement: w.role ? `(${w.role})` : '' })),
      });
    }
    setSeed({ form: f, safetyReportId: safety ? safety.id : '' });
    setOpenId('new');
  };

  if (openId) {
    const report = openId === 'new' ? null : reports.find(r => r.id === openId);
    return (
      <IncidentEditor
        // One stable key: a new report becomes a saved one in place, so Email right after
        // the first save still has its modal to open.
        key="editor"
        report={report}
        seed={seed}
        options={data.options}
        drivers={data.drivers}
        recipients={data.recipients}
        notify={notify}
        onBack={() => { setOpenId(null); setSeed(null); }}
        onSaved={async (rep) => {
          const r = await load();
          if (r && r.success && rep) setOpenId(rep.id);
        }}
        onRecipients={(list) => setData(d => ({ ...d, recipients: list }))}
      />
    );
  }

  const visible = reports.filter(r => (showDeleted ? r.deleted_at : !r.deleted_at));
  const deletedCount = reports.filter(r => r.deleted_at).length;

  return (
    <div>
      <div style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: '.85rem', color: 'var(--hp-dim)', maxWidth: 620, lineHeight: 1.5 }}>
          The formal incident report for Australia Post — an injury, a near miss, an assault. Fill it in, check the
          PDF, then email it with the photos. Australia Post supplies no form, so this one is ours.
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {data.safetyReports.length > 0 && (
            <select className="input" style={{ width: 'auto' }} value="" onChange={(e) => {
              const s = data.safetyReports.find(x => x.id === e.target.value);
              if (s) startNew(s);
            }}>
              <option value="">Start from a driver's safety report…</option>
              {data.safetyReports.map(s => (
                <option key={s.id} value={s.id}>{fmtDate(s.occurred_on)} · {s.driver_name || s.driver_login} · {(s.description || '').slice(0, 50)}</option>
              ))}
            </select>
          )}
          <button className="btn btn-primary btn-sm" onClick={() => startNew(null)}><Plus size={14} /> New incident report</button>
        </div>
      </div>

      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Report</th><th style={th}>Incident</th><th style={th}>Driver</th>
              <th style={th}>Status</th><th style={th}>Last sent</th>{showDeleted && <th style={th} />}
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr><td style={{ ...td, color: 'var(--hp-dim)', textAlign: 'center', padding: 28 }} colSpan={6}>
                {showDeleted ? 'No deleted reports.' : 'No incident reports yet.'}
              </td></tr>
            )}
            {visible.map(r => {
              const f = r.form || {};
              const [label, color] = STATUS[r.status] || STATUS.draft;
              const last = (r.emails || [])[(r.emails || []).length - 1];
              return (
                <tr key={r.id} style={{ cursor: showDeleted ? 'default' : 'pointer' }} onClick={() => !showDeleted && setOpenId(r.id)}>
                  <td style={{ ...td, fontFamily: 'var(--font-mono)', fontWeight: 700 }}>{r.number}</td>
                  <td style={td}>
                    <div style={{ fontWeight: 700 }}>{optLabel(data.options.INCIDENT_TYPES, f.incidentType) || 'Incident'}</div>
                    <div style={{ color: 'var(--hp-dim)', fontSize: '.78rem' }}>{fmtDate(f.incidentDate)}{f.location ? ` · ${f.location}${f.suburb ? ', ' + f.suburb : ''}` : ''}</div>
                  </td>
                  <td style={td}>{f.driverName || '—'}</td>
                  <td style={td}><Chip text={label} color={color} /></td>
                  <td style={{ ...td, fontSize: '.78rem', color: 'var(--hp-dim)' }}>{last ? `${fmtWhen(last.at)} → ${last.to.join(', ')}` : '—'}</td>
                  {showDeleted && (
                    <td style={td}>
                      <button className="btn btn-secondary btn-sm" onClick={async (e) => {
                        e.stopPropagation();
                        const x = await api('restore', { id: r.id });
                        if (x.success) { notify(`${r.number} restored`, 'success'); load(); } else notify(x.error || 'Restore failed', 'error');
                      }}><RotateCcw size={13} /> Restore</button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {(deletedCount > 0 || showDeleted) && (
        <button className="btn btn-ghost btn-sm" onClick={() => setShowDeleted(v => !v)}>
          {showDeleted ? 'Back to reports' : `Show deleted (${deletedCount})`}
        </button>
      )}
    </div>
  );
}

// ── the form ──────────────────────────────────────────────────────────────────

function Field({ label, children, wide, hint }) {
  return (
    <div style={wide ? { gridColumn: '1 / -1' } : undefined}>
      <label style={labelStyle}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: '.74rem', color: 'var(--hp-dim)', marginTop: 4, lineHeight: 1.45 }}>{hint}</div>}
    </div>
  );
}

function IncidentEditor({ report, seed, options, drivers, recipients, notify, onBack, onSaved, onRecipients }) {
  const [form, setForm] = useState(() => (report ? { ...blankForm(), ...report.form } : seed.form));
  const [dirty, setDirty] = useState(!report);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState('');
  const [emailOpen, setEmailOpen] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => { const v = e && e.target ? e.target.value : e; setForm(f => ({ ...f, [k]: v })); setDirty(true); };
  const text = (k, props = {}) => <input className="input" style={inputStyle} value={form[k] || ''} onChange={set(k)} {...props} />;
  const long = (k, rows = 3, props = {}) => <textarea className="input" style={{ ...inputStyle, minHeight: rows * 22 + 16 }} value={form[k] || ''} onChange={set(k)} {...props} />;
  const select = (k, opts) => (
    <select className="input" style={inputStyle} value={form[k] || ''} onChange={set(k)}>
      <option value="">—</option>
      {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );

  const pickDriver = (e) => {
    const login = e.target.value;
    const d = drivers.find(x => x.login === login);
    setForm(f => ({ ...f, driverLogin: login, driverName: d ? d.name : f.driverName, driverPhone: d ? d.phone : f.driverPhone, area: f.area || (d ? d.area : '') }));
    setDirty(true);
  };

  const setWitness = (i, k, v) => { setForm(f => ({ ...f, witnesses: f.witnesses.map((w, j) => (j === i ? { ...w, [k]: v } : w)) })); setDirty(true); };

  // Save, and hand back the saved report so Email / PDF can follow straight on.
  const save = async (quiet) => {
    setSaving(true);
    const r = await api('save', { id: report ? report.id : undefined, form, safetyReportId: !report && seed ? seed.safetyReportId : undefined });
    setSaving(false);
    if (!r.success) { notify(r.error || 'Save failed', 'error'); return null; }
    setDirty(false);
    if (!quiet) notify(`${r.report.number} saved`, 'success');
    await onSaved(r.report);
    return r.report;
  };

  const downloadPdf = async () => {
    const saved = dirty || !report ? await save(true) : report;
    if (!saved) return;
    setBusy('pdf');
    const r = await api('pdf', { id: saved.id });
    setBusy('');
    if (!r.success) { notify(r.error || 'Could not build the PDF', 'error'); return; }
    const bytes = Uint8Array.from(atob(r.pdf), c => c.charCodeAt(0));
    const url = window.URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url; a.download = r.filename; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 4000);
  };

  const openEmail = async () => {
    if (dirty || !report) { const s = await save(true); if (!s) return; }
    setEmailOpen(true);
  };

  const uploadPhotos = async (files) => {
    if (!report) { notify('Save the report first, then add photos', 'error'); return; }
    setBusy('photo');
    let ok = 0;
    for (const file of files) {
      const buf = await uprightJpegBytes(file, 1800, 0.84);
      if (!buf) { notify(`Could not read ${file.name}`, 'error'); continue; }
      const b64 = btoa(new Uint8Array(buf).reduce((acc, b) => acc + String.fromCharCode(b), ''));
      const r = await api('addPhoto', { id: report.id, image: b64, caption: '' });
      if (r.success) ok++; else notify(r.error || 'Upload failed', 'error');
    }
    setBusy('');
    if (ok) { notify(`${ok} photo${ok > 1 ? 's' : ''} added`, 'success'); await onSaved(report); }
  };

  const photoAction = async (p, payload) => {
    const r = await api('updatePhoto', { id: report.id, photoId: p.id, ...payload });
    if (r.success) await onSaved(report); else notify(r.error || 'Failed', 'error');
  };

  const remove = async () => {
    if (!report) { onBack(); return; }
    const r = await api('remove', { id: report.id });
    if (!r.success) { notify(r.error || 'Delete failed', 'error'); return; }
    notify(`${report.number} deleted — Show deleted → Restore brings it back`, 'success');
    await onSaved(null);
    onBack();
  };

  const [statusLabel, statusColor] = STATUS[report ? report.status : 'draft'] || STATUS.draft;
  const photos = report ? report.photos || [] : [];
  const O = options;
  const seriousTreatment = ['admitted'].includes(form.treatment);

  return (
    <div>
      {/* Action bar — stays put while the long form scrolls under it. */}
      <div style={{ ...card, position: 'sticky', top: 0, zIndex: 5, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button className="btn btn-ghost btn-sm" onClick={onBack}><ArrowLeft size={14} /> All reports</button>
        <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 800 }}>{report ? report.number : 'New report'}</div>
        <Chip text={statusLabel} color={statusColor} />
        {dirty && <span style={{ fontSize: '.78rem', color: '#d97706', fontWeight: 700 }}>Unsaved changes</span>}
        <div style={{ flex: 1 }} />
        <button className="btn btn-secondary btn-sm" onClick={() => save(false)} disabled={saving || !dirty}>
          {saving ? <Loader size={14} /> : <Save size={14} />} Save
        </button>
        <button className="btn btn-secondary btn-sm" onClick={downloadPdf} disabled={!!busy || saving}>
          {busy === 'pdf' ? <Loader size={14} /> : <FileText size={14} />} PDF
        </button>
        <button className="btn btn-primary btn-sm" onClick={openEmail} disabled={saving}>
          <Mail size={14} /> Email to Australia Post
        </button>
      </div>

      <div style={card}>
        <h4 style={{ margin: '0 0 12px' }}>The incident</h4>
        <div style={grid}>
          <Field label="Type of incident">{select('incidentType', O.INCIDENT_TYPES)}</Field>
          <Field label="Date">{text('incidentDate', { type: 'date' })}</Field>
          <Field label="Time (approx.)">{text('incidentTime', { type: 'time' })}</Field>
          <Field label="Where it happened">{select('locationType', O.LOCATION_TYPES)}</Field>
          <Field label="Street address / nearest address">{text('location', { placeholder: 'e.g. outside 120 Victoria Street' })}</Field>
          <Field label="Suburb">{text('suburb')}</Field>
          <Field label="Delivery area / contract">{text('area', { placeholder: 'e.g. DARL' })}</Field>
          <Field label="Weather, lighting, footpath">{text('conditions', { placeholder: 'e.g. fine, daylight, dry, busy footpath' })}</Field>
          <Field label="When the office was told" wide>{text('reportedToOffice', { placeholder: 'e.g. driver phoned Paul at 11:40 am the same day' })}</Field>
        </div>
      </div>

      <div style={card}>
        <h4 style={{ margin: '0 0 12px' }}>Our driver</h4>
        <div style={grid}>
          <Field label="Driver">
            <select className="input" style={inputStyle} value={form.driverLogin || ''} onChange={pickDriver}>
              <option value="">— choose —</option>
              {drivers.map(d => <option key={d.login} value={d.login}>{d.name || d.login} ({d.login})</option>)}
            </select>
          </Field>
          <Field label="Name as it appears on the report">{text('driverName')}</Field>
          <Field label="Role">{text('driverRole')}</Field>
          <Field label="Phone">{text('driverPhone')}</Field>
          <Field label="What the driver was doing at the time" wide>{long('activity', 2, { placeholder: 'e.g. walking on the footpath carrying two parcels from the van to number 120' })}</Field>
        </div>
      </div>

      <div style={card}>
        <h4 style={{ margin: '0 0 12px' }}>What happened</h4>
        <Field label="In order, in plain words" hint="Facts, not blame: what the driver saw and did, what the other person did, and what happened straight after.">
          {long('description', 7)}
        </Field>
      </div>

      <div style={card}>
        <h4 style={{ margin: '0 0 12px' }}>Injury and treatment</h4>
        <div style={grid}>
          <Field label="Was the driver injured?">{select('injured', O.YNU)}</Field>
          <Field label="Part(s) of body">{text('bodyParts', { placeholder: 'e.g. left hip, right elbow' })}</Field>
          <Field label="Treatment">{select('treatment', O.TREATMENT)}</Field>
          <Field label="Time off work?">{select('lostTime', O.YNU)}</Field>
          <Field label="The injury" wide>{long('injuryDetail', 2)}</Field>
          <Field label="Treatment details" wide>{long('treatmentDetail', 2, { placeholder: 'Who treated them and where, e.g. St Vincent\'s ED; GP certificate attached' })}</Field>
          <Field label="Days off">{text('daysOff', { placeholder: 'e.g. 2 shifts' })}</Field>
          <Field label="Return to work">{text('returnToWork', { placeholder: 'e.g. back on full duties Mon 20 Oct' })}</Field>
        </div>
      </div>

      <div style={card}>
        <h4 style={{ margin: '0 0 12px' }}>The other party</h4>
        <div style={grid}>
          <Field label="Who">{text('tpType', { placeholder: 'e.g. e-bike rider (food delivery)' })}</Field>
          <Field label="Name">{text('tpName', { placeholder: 'if known' })}</Field>
          <Field label="Phone / contact">{text('tpPhone')}</Field>
          <Field label="At the scene">{select('tpStayed', O.TP_STAYED)}</Field>
          <Field label="Description of the person and the bike / vehicle" wide>{long('tpDescription', 2, { placeholder: 'Clothing, bag/branding, bike colour, rego if any, direction they went' })}</Field>
        </div>
      </div>

      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h4 style={{ margin: 0 }}>Witnesses</h4>
          <button className="btn btn-secondary btn-sm" onClick={() => { setForm(f => ({ ...f, witnesses: [...f.witnesses, { name: '', phone: '', statement: '' }] })); setDirty(true); }}>
            <Plus size={13} /> Add witness
          </button>
        </div>
        {form.witnesses.length === 0 && <div style={{ fontSize: '.84rem', color: 'var(--hp-dim)' }}>None recorded.</div>}
        {form.witnesses.map((w, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8, marginBottom: 10, paddingBottom: 10, borderBottom: '1px solid var(--hp-border)' }}>
            <input className="input" placeholder="Name" value={w.name} onChange={(e) => setWitness(i, 'name', e.target.value)} />
            <input className="input" placeholder="Phone" value={w.phone} onChange={(e) => setWitness(i, 'phone', e.target.value)} />
            <div style={{ display: 'flex', gap: 6, gridColumn: '1 / -1' }}>
              <textarea className="input" style={{ flex: 1, minHeight: 50 }} placeholder="What they saw" value={w.statement} onChange={(e) => setWitness(i, 'statement', e.target.value)} />
              <button className="btn btn-ghost btn-sm" title="Remove witness" onClick={() => { setForm(f => ({ ...f, witnesses: f.witnesses.filter((_, j) => j !== i) })); setDirty(true); }}><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
      </div>

      <div style={card}>
        <h4 style={{ margin: '0 0 12px' }}>Police and SafeWork</h4>
        <div style={grid}>
          <Field label="Reported to police?">{select('policeReported', O.YNU)}</Field>
          <Field label="Police event number">{text('policeEvent', { placeholder: 'e.g. E12345678' })}</Field>
          <Field label="Station / officer">{text('policeStation')}</Field>
          <Field label="Notifiable to SafeWork NSW?"
            hint="Notifiable if it caused a serious injury — e.g. admission to hospital as an inpatient, a serious head injury, a fracture needing immediate treatment. SafeWork NSW 13 10 50.">
            {select('safeworkNotifiable', O.YNU)}
          </Field>
          <Field label="SafeWork reference">{text('safeworkRef')}</Field>
        </div>
        {seriousTreatment && form.safeworkNotifiable !== 'yes' && (
          <div style={{ marginTop: 10, fontSize: '.82rem', color: '#d97706', display: 'flex', gap: 6, alignItems: 'center' }}>
            <AlertTriangle size={14} /> Admission to hospital is usually a notifiable incident — check before sending.
          </div>
        )}
      </div>

      <div style={card}>
        <h4 style={{ margin: '0 0 12px' }}>Parcels, equipment and actions</h4>
        <div style={grid}>
          <Field label="Parcels / scanner / equipment affected" wide>{long('articlesAffected', 2, { placeholder: 'e.g. no parcels damaged; scanner screen cracked; rest of the run covered by …' })}</Field>
          <Field label="Immediate actions taken" wide>{long('immediateActions', 3)}</Field>
          <Field label="What we are doing to stop it happening again" wide>{long('preventiveActions', 3)}</Field>
        </div>
      </div>

      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h4 style={{ margin: 0 }}>Photos</h4>
          <button className="btn btn-secondary btn-sm" disabled={!report || busy === 'photo'} onClick={() => fileRef.current && fileRef.current.click()}
            title={report ? '' : 'Save the report first'}>
            {busy === 'photo' ? <Loader size={13} /> : <Upload size={13} />} Add photos
          </button>
          <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }}
            onChange={(e) => { const fs = [...e.target.files]; e.target.value = ''; if (fs.length) uploadPhotos(fs); }} />
        </div>
        {!report && <div style={{ fontSize: '.84rem', color: 'var(--hp-dim)' }}>Save the report first, then add photos of the scene and the injury.</div>}
        {report && photos.length === 0 && <div style={{ fontSize: '.84rem', color: 'var(--hp-dim)' }}>No photos yet. They go into the PDF and are attached to the email.</div>}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 10 }}>
          {photos.map((p, i) => (
            <div key={p.id} style={{ border: '1px solid var(--hp-border)', borderRadius: 8, overflow: 'hidden' }}>
              <a href={p.url} target="_blank" rel="noreferrer"><img src={p.url} alt={p.caption || `Photo ${i + 1}`} style={{ width: '100%', height: 130, objectFit: 'cover', display: 'block' }} /></a>
              <div style={{ display: 'flex', gap: 4, padding: 6 }}>
                <input className="input" style={{ flex: 1, fontSize: '.78rem', padding: '3px 6px' }} placeholder={`Photo ${i + 1} caption`} defaultValue={p.caption}
                  onBlur={(e) => e.target.value !== (p.caption || '') && photoAction(p, { caption: e.target.value })} />
                <button className="btn btn-ghost btn-sm" title="Remove from this report" onClick={() => photoAction(p, { remove: true })}><Trash2 size={12} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={card}>
        <h4 style={{ margin: '0 0 12px' }}>Prepared by</h4>
        <div style={grid}>
          <Field label="Name">{text('preparedBy')}</Field>
          <Field label="Position">{text('preparedPosition', { placeholder: 'e.g. Director' })}</Field>
          <Field label="Phone">{text('preparedPhone')}</Field>
          <Field label="Email">{text('preparedEmail', { type: 'email' })}</Field>
        </div>
      </div>

      {report && (report.emails || []).length > 0 && (
        <div style={card}>
          <h4 style={{ margin: '0 0 10px' }}>Sent</h4>
          {report.emails.slice().reverse().map((m, i) => (
            <div key={i} style={{ fontSize: '.83rem', padding: '6px 0', borderBottom: '1px solid var(--hp-border)', display: 'flex', gap: 8, alignItems: 'baseline' }}>
              <CheckCircle size={13} style={{ color: '#16a34a', flexShrink: 0 }} />
              <div>
                <b>{fmtWhen(m.at)}</b> by {m.by} → {m.to.join(', ')}
                {(m.cc || []).length > 0 && <span style={{ color: 'var(--hp-dim)' }}> · cc {m.cc.join(', ')}</span>}
                <div style={{ color: 'var(--hp-dim)', fontSize: '.78rem' }}>{m.subject}{m.photos ? ` · ${m.photos} photo${m.photos > 1 ? 's' : ''}` : ''}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {report && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 30 }}>
          <button className="btn btn-ghost btn-sm" style={{ color: '#ef4444' }} onClick={remove}><Trash2 size={13} /> Delete report</button>
        </div>
      )}

      {emailOpen && report && (
        <EmailModal
          report={report}
          form={form}
          options={options}
          recipients={recipients}
          notify={notify}
          onRecipients={onRecipients}
          onClose={() => setEmailOpen(false)}
          onSent={async () => { setEmailOpen(false); await onSaved(report); }}
        />
      )}
    </div>
  );
}

// ── email ─────────────────────────────────────────────────────────────────────

function EmailModal({ report, form, options, recipients, notify, onRecipients, onClose, onSent }) {
  const [book, setBook] = useState(recipients || []);
  const [picked, setPicked] = useState({});        // email → 'to' | 'cc' | 'bcc'
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [subject, setSubject] = useState(
    `PH Bouveng Pty Ltd — Incident Report ${report.number}: ${optLabel(options.INCIDENT_TYPES, form.incidentType) || 'incident'}`
    + `${form.incidentDate ? ' — ' + fmtDate(form.incidentDate) : ''}${form.driverName ? ' — ' + form.driverName : ''}`);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const saveBook = async (list) => {
    setBook(list);
    const r = await api('recipientsSave', { recipients: list });
    if (r.success) { setBook(r.recipients); onRecipients(r.recipients); }
    else notify(r.error || 'Failed to save the recipient', 'error');
  };
  const addRecipient = () => {
    const email = newEmail.trim().toLowerCase();
    if (!EMAIL_RE.test(email)) { notify('Enter a valid email address', 'error'); return; }
    if (book.some(r => r.email === email)) { setPicked(p => ({ ...p, [email]: 'to' })); setNewEmail(''); setNewName(''); return; }
    saveBook([...book, { name: newName.trim(), email }]);
    setPicked(p => ({ ...p, [email]: 'to' }));
    setNewName(''); setNewEmail('');
  };

  const slot = (s) => Object.keys(picked).filter(e => picked[e] === s);
  const to = slot('to'), cc = slot('cc'), bcc = slot('bcc');
  const total = to.length + cc.length + bcc.length;
  const photoCount = (report.photos || []).length;

  const send = async () => {
    if (!to.length) { notify('Put at least one recipient in To', 'error'); return; }
    setSending(true);
    const r = await api('send', { id: report.id, to, cc, bcc, subject: subject.trim(), message: message.trim() });
    setSending(false);
    if (!r.success) { notify(r.error || 'Failed to send', 'error'); return; }
    const miss = r.photosSkipped ? ` — ${r.photosSkipped} photo${r.photosSkipped > 1 ? 's' : ''} too large to attach` : '';
    notify(`${report.number} emailed to ${r.to.join(', ')}${r.photos ? ` with ${r.photos} photo${r.photos > 1 ? 's' : ''}` : ''}${miss}`, miss ? 'error' : 'success');
    onSent();
  };

  return (
    <div style={modalOverlay} onClick={(e) => e.target === e.currentTarget && !sending && onClose()}>
      <div style={modalCard}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 20px', borderBottom: '1px solid var(--hp-border)' }}>
          <h3 style={{ margin: 0, fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: 8 }}><Mail size={17} style={{ color: 'var(--hp-gold)' }} /> Email {report.number}</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={sending}><X size={16} /></button>
        </div>
        <div style={{ padding: 20 }}>
          <label style={labelStyle}>Recipients</label>
          {book.length === 0 && <div style={{ color: 'var(--hp-dim)', fontSize: '.85rem', marginBottom: 6 }}>No saved recipients yet — add one below.</div>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
            {book.map(r => (
              <div key={r.email} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.87rem', cursor: 'pointer', flex: 1, minWidth: 0 }}>
                  <input type="checkbox" checked={!!picked[r.email]}
                    onChange={(e) => setPicked(p => { const n = { ...p }; if (e.target.checked) n[r.email] = 'to'; else delete n[r.email]; return n; })} />
                  <span style={{ fontWeight: 700 }}>{r.name || r.email}</span>
                  {r.name && <span style={{ color: 'var(--hp-dim)', fontSize: '.8rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.email}</span>}
                </label>
                {picked[r.email] && (
                  <select className="input" aria-label={`Send ${r.name || r.email} as`} value={picked[r.email]} style={{ width: 'auto', padding: '3px 6px', fontSize: '.8rem' }}
                    onChange={(e) => setPicked(p => ({ ...p, [r.email]: e.target.value }))}>
                    <option value="to">To</option><option value="cc">Cc</option><option value="bcc">Bcc</option>
                  </select>
                )}
              </div>
            ))}
          </div>

          <label style={labelStyle}>Add recipient</label>
          <div style={{ display: 'flex', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
            <input className="input" style={{ flex: '1 1 150px' }} value={newName} placeholder="Name" onChange={(e) => setNewName(e.target.value)} />
            <input className="input" style={{ flex: '1 1 210px' }} type="email" value={newEmail} placeholder="email@auspost.com.au" onChange={(e) => setNewEmail(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addRecipient()} />
            <button className="btn btn-secondary btn-sm" onClick={addRecipient} disabled={!newEmail.trim()}><Plus size={13} /> Add</button>
          </div>
          <div style={{ fontSize: '.75rem', color: 'var(--hp-dim)', marginBottom: 14 }}>Added recipients are saved for next time, in the same address book as the Issue Register.</div>

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Subject</label>
            <input className="input" style={inputStyle} value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Message (optional — above the summary)</label>
            <textarea className="input" style={{ ...inputStyle, minHeight: 70 }} value={message} onChange={(e) => setMessage(e.target.value)}
              placeholder="e.g. Hi — please see the attached incident report for one of our drivers on Friday." />
          </div>
          <div style={{ fontSize: '.8rem', color: 'var(--hp-dim)', marginBottom: 14, lineHeight: 1.5 }}>
            Attached: <b>{report.number} Incident Report.pdf</b>{photoCount ? ` and ${photoCount} photo${photoCount > 1 ? 's' : ''}` : ''}.
            The email body carries a short summary. Paul gets a blind copy, and replies go to enquiriesphb@gmail.com.
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary" onClick={onClose} disabled={sending}>Cancel</button>
            <button className="btn btn-primary" onClick={send} disabled={sending || !to.length}>
              {sending ? <><Loader size={14} /> Sending…</> : <><Send size={14} /> Send to {total || '…'} recipient{total === 1 ? '' : 's'}</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
